/**
 * MONSTER TOWN — INTERIOR AMBIENCE. OWNED BY THE INTERIORS CREW.
 *
 * WHAT A ROOM SITS IN. Every interior in this game — the four house rooms, both
 * venues and the town shop — used to be drawn as a lit box floating in the
 * outdoor sky gradient, with 60-85% of an iPad frame filled by nothing at all.
 * That single fact was the strongest "unfinished 3D viewport" signal in the
 * build and it hit three of the game's four scene types.
 *
 * This file is the fix, and it is deliberately scene-agnostic: it reacts to
 * WHATEVER scene is awake and keys off `SceneNode.def.kind === 'interior'`, so
 * a venue added later as a data drop gets the whole treatment for free.
 *
 *   1. BACKDROP    the sky is swapped for a warm cosy gradient, and the outdoor
 *                  fog (tuned for a 60-unit town) is switched off indoors.
 *   2. SURROUND    the room stands on a big soft playroom rug with a vignette
 *                  falling off to the edges of the screen, so the frame is
 *                  never empty and the eye is pushed back to the room.
 *   3. GROUNDING   a soft contact shadow under the room's plinth, sized to the
 *                  room, so it sits on the rug instead of hovering over it.
 *   4. FRAMING     the camera is told the room's bounds, which clamps the zoom
 *                  ladder to framings that actually contain the room (see
 *                  `IsoCamera.setFitBounds`).
 *
 * ONE set of objects is reused for every interior — they are parented to the
 * renderer's scene, not to a scene node, so the draw-call cost is three quads
 * total no matter how many rooms exist.
 */

import * as THREE from 'three'
import { engine } from '../core/engine'
import { world, type SceneNode } from '../core/scene'
import type { SceneId } from '../core/types'
import { planeGeo } from '../render/kit'
import { app, type GameContext } from '../app/context'
import { isRoom } from './rooms'
import {
  baseTexture,
  floorTexture,
  hazeTexture,
  interiorSkyTexture,
  shadowTexture,
  stageLook,
  vignetteTexture,
  type StageLook,
} from './stageArt'

/**
 * How far the FLOOR reaches. It only has to outrun the widest framing the zoom
 * ladder can reach, and it is a tiled pattern rather than one stretched image,
 * so making it generous costs nothing — no part of it is ever magnified.
 */
const FLOOR_SPAN = 220
/** How many world units one repeat of the floor pattern covers. */
const FLOOR_UNITS_PER_REPEAT = 6
/**
 * ============ THE TRAY IS FITTED TO THE ROOM, NOT A MULTIPLE OF IT ============
 *
 * VR1 BLOCKER. The stage under the room was a square scaled by
 * `max(width, depth) * 1.72`, floored at 13 units — one number, applied to the
 * LONGER side, applied to BOTH sides. On the kitchen and on venue #3 that put a
 * huge oval reaching "well OUTSIDE the room's own walls on all four sides", and
 * on any room that is not square the overhang was different on each axis. A
 * fixed multiple cannot help doing that: the margin it produces scales with the
 * room, so the bigger the room the more floor covering there is around it.
 *
 * A MARGIN IS A MARGIN. The tray is now the room's own footprint plus a constant
 * lip on every side, per axis. The room's plinth already overhangs the walls by
 * `PLINTH + WALL_T + WALL_GAP` = 0.68 (see shell.ts), so 1.2 leaves about half a
 * unit of visible tray outside the plinth, at every room size — a lip, which is
 * what a model on a tray looks like, and which cannot grow into a carpet.
 */
const BASE_MARGIN = 1.2
/**
 * THE ROOM SITS ON THE TRAY. The old stack put the rug 0.26 below the plinth's
 * own underside, so the dollhouse hovered over its rug with daylight under it at
 * every isometric pitch. Everything here is now within 8cm of the plinth foot:
 * surround, tray, contact shadow, plinth (-0.36), in that order, bottom to top.
 */
const FLOOR_Y = -0.44
const HAZE_Y = -0.435
const BASE_Y = -0.42
const SHADOW_Y = -0.4
/**
 * HOW FAR THE HORIZON IS FROM THE ROOM, as a multiple of the room's longer side.
 * Big enough that the fully-transparent middle of `hazeTexture` clears the tray
 * at every zoom step, small enough that its opaque rim lands INSIDE the frame —
 * which is the whole point of it, and the exact mistake the vignette made when
 * it was drawn on a 110-unit quad and its ring landed 55 units off screen.
 */
const HAZE_SPAN = 3.2


// ---------------------------------------------------------------------------
// the objects
// ---------------------------------------------------------------------------

let rig: THREE.Group | null = null
let floor: THREE.Mesh | null = null
/** The horizon: where the surround stops being a floor. See `hazeTexture`. */
let haze: THREE.Mesh | null = null
let base: THREE.Mesh | null = null
let vignette: THREE.Mesh | null = null
let shadow: THREE.Mesh | null = null
/** The last look applied, so a re-entered room is not repainted for nothing. */
let lookKey = ''

function buildRig(): THREE.Group {
  const g = new THREE.Group()
  g.name = 'interiorAmbience'
  g.visible = false

  // The playroom floor. Fixed size and a fixed world tiling, so it never has to
  // be rescaled per room and its pattern is the same size in a 6x6 bathroom and
  // a 20-unit venue — one draw call for the whole thing.
  const ftex = floorTexture().clone()
  ftex.wrapS = THREE.RepeatWrapping
  ftex.wrapT = THREE.RepeatWrapping
  ftex.repeat.set(FLOOR_SPAN / FLOOR_UNITS_PER_REPEAT, FLOOR_SPAN / FLOOR_UNITS_PER_REPEAT)
  ftex.needsUpdate = true
  floor = new THREE.Mesh(
    planeGeo(FLOOR_SPAN, FLOOR_SPAN),
    // UNLIT. The design IS the lighting; running it through a toon shader means
    // the sun re-lights it and the whole surround changes brightness every time
    // a kid turns the camera 90 degrees.
    new THREE.MeshBasicMaterial({ map: ftex, fog: false }),
  )
  floor.position.y = FLOOR_Y
  floor.receiveShadow = false
  floor.name = 'interiorFloor'
  g.add(floor)

  /*
   * THE HORIZON, between the floor and the tray. A unit quad scaled to the room
   * (see `HAZE_SPAN`), transparent over the room and ramping to `StageLook.far`
   * at its rim, so the surround dissolves into the backdrop wall instead of
   * running to the edge of the glass as an infinite tiled plane. One draw call.
   */
  haze = new THREE.Mesh(
    planeGeo(1, 1),
    new THREE.MeshBasicMaterial({
      map: hazeTexture(),
      transparent: true,
      depthWrite: false,
      fog: false,
    }),
  )
  haze.renderOrder = -4
  haze.position.y = HAZE_Y
  haze.receiveShadow = false
  haze.name = 'interiorHorizon'
  g.add(haze)

  // A UNIT quad, scaled to the room in `enterInterior`. One geometry, one
  // material, one draw call, whatever room it is standing under.
  base = new THREE.Mesh(
    planeGeo(1, 1),
    new THREE.MeshBasicMaterial({
      map: baseTexture(),
      // The tray is a bounded object with transparent corners lying ON the
      // surround.
      transparent: true,
      depthWrite: false,
      fog: false,
    }),
  )
  base.renderOrder = -3
  base.position.y = BASE_Y
  // NOT a shadow receiver. The sun is a low directional light, so at half the
  // camera rotations the furniture threw long hard streaks straight off the
  // plinth and across the backdrop — smears with nothing casting them, because
  // whatever cast them is a metre and a half above and behind. The room's own
  // floor still receives; the world it stands on does not.
  base.receiveShadow = false
  base.name = 'interiorBase'
  g.add(base)

  shadow = new THREE.Mesh(
    planeGeo(1, 1),
    new THREE.MeshBasicMaterial({
      map: shadowTexture(),
      transparent: true,
      depthWrite: false,
      fog: false,
    }),
  )
  shadow.position.y = SHADOW_Y
  shadow.name = 'interiorRoomShadow'
  g.add(shadow)

  rig = g
  return g
}

/**
 * THE VIGNETTE IS A CHILD OF THE CAMERA, not a quad on the ground.
 *
 * First attempt put it on the same 110-unit ground plane as the rug, and its
 * dark ring landed 55 world units out — nowhere near the screen — so the only
 * part ever visible was the transparent middle and the corners of the frame
 * stayed as bright as the room. A vignette is a screen-space idea: it has to be
 * a fraction of the VIEWPORT, and the viewport changes with every zoom step, so
 * the only place it can live is on the camera itself, rescaled per frame from
 * the orthographic frustum.
 */
function buildVignette(camera: THREE.OrthographicCamera): THREE.Mesh {
  const m = new THREE.Mesh(
    planeGeo(1, 1),
    new THREE.MeshBasicMaterial({
      map: vignetteTexture(),
      transparent: true,
      depthWrite: false,
      depthTest: false,
      side: THREE.DoubleSide,
      // Unlit and unfogged on purpose: a vignette that responded to the sun
      // would swing in brightness every time a kid turned the camera.
      fog: false,
    }),
  )
  // planeGeo lies in XZ facing +Y; stand it up to face the camera down -Z.
  m.rotation.x = Math.PI / 2
  m.position.set(0, 0, -1)
  m.renderOrder = 1000
  m.frustumCulled = false
  m.visible = false
  m.name = 'interiorVignette'
  camera.add(m)
  return m
}

// ---------------------------------------------------------------------------
// applying it
// ---------------------------------------------------------------------------

interface Restore {
  background: THREE.Color | THREE.Texture | null
  fog: THREE.Fog | THREE.FogExp2 | null
}

let restore: Restore | null = null
let appliedTo: SceneId | null = null
let lastAspectKey = ''

function roomBounds(node: SceneNode): { w: number; d: number; h: number; cx: number; cz: number } {
  const g = node.grid
  const w = g.spec.width * g.tileSize
  const d = g.spec.depth * g.tileSize
  return {
    w,
    d,
    h: Math.max(2.2, g.levelHeight * 2.2),
    cx: g.originX + w / 2,
    cz: g.originZ + d / 2,
  }
}

/** Paint the stage in this room's own colours. See `stageLook` in stageArt.ts. */
function applyLook(look: StageLook): void {
  const paint = (m: THREE.Mesh | null, colour: number): void => {
    if (m === null) return
    const mat = m.material as THREE.MeshBasicMaterial
    mat.color.setHex(colour)
  }
  // ONE texture each, MULTIPLIED by the room's colour — so seven interiors cost
  // the same two canvases and the same two draw calls as one did, and the zoo no
  // longer stands on the kitchen's floor.
  paint(floor, look.ground)
  paint(base, look.base)
  paint(haze, look.far)
}

function enterInterior(ctx: GameContext, node: SceneNode): void {
  const scene = ctx.renderer.scene
  if (restore === null) {
    restore = { background: scene.background as THREE.Color | THREE.Texture | null, fog: scene.fog }
  }
  const look = stageLook(String(node.def.id))
  scene.background = interiorSkyTexture(look)
  // The outdoor fog starts at 55 units and the camera sits 60 away, so indoors
  // it just washes the far half of the stage back to sky blue. Off it goes.
  scene.fog = null

  const b = roomBounds(node)
  const g = rig ?? buildRig()
  g.visible = true
  g.position.set(b.cx, 0, b.cz)
  if (lookKey !== look.key) {
    lookKey = look.key
    applyLook(look)
  }
  if (base !== null) {
    // PER AXIS. A square scaled by the longer side is what put half a metre of
    // floor covering on two sides of the kitchen and two metres on the others.
    base.scale.set(b.w + BASE_MARGIN * 2, 1, b.d + BASE_MARGIN * 2)
  }
  if (shadow !== null) {
    // Hugs the room. At 1.9x it was a bruise the size of the tray.
    shadow.scale.set(b.w + 2.2, 1, b.d + 2.2)
  }
  if (haze !== null) {
    // SQUARE, and off the LONGER side. The transparent middle has to clear the
    // tray on both axes; a per-axis fit would put the falloff on top of the
    // room's own short side.
    const span = Math.max(b.w, b.d) * HAZE_SPAN
    haze.scale.set(span, 1, span)
  }
  /**
   * ONE CREW PER FRAME. The venue crew fits its own rooms (its system runs at
   * order 51, ahead of this one at 63) and it knows things about a venue we do
   * not — where the counter is, how big a customer's face has to read. So we
   * only claim the framing of a room nobody else has claimed this scene: house
   * rooms always, anything else only if the camera is still unfitted. Two crews
   * yanking the same camera every frame is how you get a jittering zoom.
   */
  if (isRoom(node.def.id) || ctx.camera.fitBounds === null) {
    ctx.camera.setFitBounds({ w: b.w, h: b.h, d: b.d })
  }
}

function leaveInterior(ctx: GameContext): void {
  const scene = ctx.renderer.scene
  if (restore !== null) {
    scene.background = restore.background
    scene.fog = restore.fog
    restore = null
  }
  if (rig !== null) rig.visible = false
  ctx.camera.setFitBounds(null)
}

/**
 * Watch the awake scene and keep the ambience matched to it.
 *
 * Polling the active scene id (rather than listening for `scene:entered`) is
 * deliberate: the harness swaps scenes with `setActiveImmediate`, which emits
 * nothing, and a screenshot taken through that path must look exactly like the
 * one a kid walking through a door sees.
 */
export function installInteriorAmbience(ctx: GameContext): void {
  ctx.renderer.scene.add(buildRig())
  // The camera has to be IN the scene graph for its children to be rendered.
  // Adding it changes no transform — its parent is the identity scene root.
  ctx.renderer.scene.add(ctx.camera.camera)
  vignette = buildVignette(ctx.camera.camera)

  if (import.meta.env.DEV) {
    const w = window as unknown as Record<string, unknown>
    w['__mtInterior'] = { ambience: ambienceDebug, fit: interiorFitReport, stage: stageProbe }
  }

  engine.addSystem({
    id: 'interior.ambience',
    order: 63,
    render: () => {
      const id = world.activeSceneId
      const node = id === null ? undefined : world.get(id)
      const interior = node !== undefined && node.def.kind === 'interior'
      /**
       * THE CREATOR IS NOT AN INTERIOR, WHATEVER SCENE IT OPENED FROM.
       *
       * ROUND-3 BLOCKER. The monster crew's creator hides the awake scene's own
       * layers and drops its own cream backdrop card nine units behind the
       * monster — but this rig is parented to the RENDERER's scene, not to the
       * node, so it survived the takeover. A ground plane two hundred units
       * across is nearer than a card nine units back over almost the whole
       * frame, so it drew straight over the backdrop and the creator inherited
       * the interior surround ("the monster creator inherits the same brown mud
       * backdrop"). Anything that hides the world for a moment has to hide what
       * the world stands on too.
       *
       * VISIBILITY ONLY — the scene state is left applied. Running the full
       * `leaveInterior` here would call `setFitBounds(null)` and yank the camera
       * out from under the creator's own framing mid-takeover.
       */
      const creator = app.phase === 'monsterCreator'
      if (rig !== null) rig.visible = interior && !creator
      // The fitted zoom ladder depends on the aspect ratio, and an iPad rotated
      // to portrait must re-fit rather than keep the landscape framing.
      // right/top IS the aspect, and unlike either on its own it does not move
      // while a zoom is still easing.
      const cam = ctx.camera.camera
      const aspectKey = interior ? `${Math.round((cam.right / (cam.top || 1)) * 100)}` : ''
      /**
       * THE VIGNETTE IS SIZED TO THE VIEW, EVERY FRAME.
       *
       * First attempt drew it on the same 110-unit quad as the rug, so its dark
       * ring landed 55 world units out — miles off screen — and the only part
       * anyone ever saw was the transparent middle. A vignette has to be a
       * fraction of the VIEWPORT to exist at all, and the viewport moves with
       * the zoom, so this is a per-frame two-line update rather than a constant.
       */
      if (vignette !== null) {
        vignette.visible = interior && !creator
        if (interior) vignette.scale.set(Math.abs(cam.right) * 2.02, 1, Math.abs(cam.top) * 2.02)
      }
      if (id === appliedTo && aspectKey === lastAspectKey) return
      appliedTo = id
      lastAspectKey = aspectKey
      if (interior && node !== undefined) enterInterior(ctx, node)
      else leaveInterior(ctx)
    },
  })
}

/** Dev/QA probe: what the ambience thinks it is doing right now. */
export function ambienceDebug(): Record<string, unknown> {
  return { active: appliedTo, visible: rig?.visible ?? false }
}

/**
 * DEV/QA PROBE FOR THE STAGE ITSELF, added for the vr1 "the rug is bigger than
 * the building" defect. "Well outside the walls on all four sides" is a
 * MEASUREMENT, and until this existed nobody could take it — the stage lives on
 * the renderer's scene, not on the node, so a tool walking the scene graph never
 * found it. `lipX`/`lipZ` are how far the tray reaches past the room's own wall
 * line, per axis, in world units.
 */
export function stageProbe(): Record<string, unknown> | null {
  const id = world.activeSceneId
  const node = id === null ? undefined : world.get(id)
  if (node === undefined || node.def.kind !== 'interior' || base === null) return null
  const g = node.grid
  const roomW = g.spec.width * g.tileSize
  const roomD = g.spec.depth * g.tileSize
  const look = stageLook(String(node.def.id))
  const hex = (n: number): string => `#${n.toString(16).padStart(6, '0')}`
  return {
    scene: String(node.def.id),
    roomW,
    roomD,
    trayW: base.scale.x,
    trayD: base.scale.z,
    lipX: (base.scale.x - roomW) / 2,
    lipZ: (base.scale.z - roomD) / 2,
    ground: hex(look.ground),
    base: hex(look.base),
    hue: look.key,
  }
}

/**
 * DEV-ONLY FIT PROBE, for `tools/interior-fit.mjs`.
 *
 * "Furniture clips through furniture" is not a thing you can settle by looking
 * at an isometric screenshot — overlapping on screen is not overlapping in
 * space. This measures it: the world bounding box of each piece's ART against
 * the footprint the grid actually reserved for it. Anything over ~0.06 of a
 * tile is art that is physically bigger than its own square, which is the only
 * way two correctly-placed pieces can ever intersect.
 */
export function interiorFitReport(): Array<Record<string, unknown>> {
  const id = world.activeSceneId
  const node = id === null ? undefined : world.get(id)
  if (node === undefined) return []
  const box = new THREE.Box3()
  const rows: Array<Record<string, unknown>> = []
  for (const p of node.grid.all()) {
    const art = node.art.get(p.instanceId)
    if (art === undefined) continue
    box.setFromObject(art)
    if (!isFinite(box.min.x)) continue
    const g = node.grid
    const c = g.footprintCenter(p.anchor, p.footprint, p.rot)
    const rotated = p.rot % 2 === 1
    const fw = (rotated ? p.footprint.d : p.footprint.w) * g.tileSize
    const fd = (rotated ? p.footprint.w : p.footprint.d) * g.tileSize
    const over = {
      xMin: c.x - fw / 2 - box.min.x,
      xMax: box.max.x - (c.x + fw / 2),
      zMin: c.z - fd / 2 - box.min.z,
      zMax: box.max.z - (c.z + fd / 2),
    }
    const worst = Math.max(over.xMin, over.xMax, over.zMin, over.zMax)
    rows.push({
      defId: p.defId,
      anchor: `${p.anchor.gx},${p.anchor.gz}`,
      over: worst,
      sides: Object.entries(over)
        .filter(([, v]) => v > 0.06)
        .map(([k, v]) => `${k} ${(v as number).toFixed(2)}`)
        .join(' '),
    })
  }
  return rows
}
